/**
 * System design review fixture — Design a URL Shortener seed (spec §8.5).
 * Shares the `ReviewSubmission`/`ReviewComment` model owned by `./coding`.
 */

import type { SubmittedReviewRow } from '../../review/contracts'

export const MOCK_SD_REVIEW: SubmittedReviewRow = {
  id: 'sd-url-shortener',
  type: 'sd',
  title: 'Design a URL Shortener',
  chips: ['Whiteboard + notes', '1st attempt'],
  submittedWhen: 'Submitted 2 hours ago',
  sprint: 'Sprint 14',
  workedAtMs: Date.parse('2026-08-13T14:00:00.000Z'),
  ranking: {
    position: 1,
    reason: 'Ranked first — the struggle signals outweigh the solid structure:',
    chips: [
      {
        icon: '▲',
        label: 'Marked "Again"',
        tone: 'red',
        detail:
          'Self-rated Again in the post-mortem — the strongest single ranking signal.',
      },
      {
        icon: '😤',
        label: 'Froze near the end',
        tone: 'red',
        detail:
          '"It threw me — I froze for a couple minutes near the end." Tone analysis flags distress in the reflection.',
      },
      {
        icon: '⚠',
        label: 'Ran long: 48:37',
        tone: 'amber',
        detail: 'Past the target band; deep dives were cut short as a result.',
      },
      {
        icon: '?',
        label: 'Asked a question',
        tone: 'blue',
        detail:
          'Post-mortem asks how to fit two deep dives into 45 minutes — waiting on your answer.',
      },
    ],
  },
  pacing: {
    total: '48:37',
    totalNote: 'deep dives cut short',
    // Spec §8.5: "1 company row by default" for the SD seed.
    companyRows: [
      { company: 'Google', range: '35–45 min', verdict: 'Over range' },
    ],
    topicAvg: {
      label: 'TOPIC AVG · SYSTEM DESIGN',
      value: '41 min',
      delta: '+7:37 vs avg',
      over: true,
    },
    subtopicAvg: {
      label: 'SUBTOPIC AVG · READ-HEAVY STORAGE',
      value: '44 min',
      delta: '+4:37 vs avg',
      over: true,
    },
  },
  // Coding-only (LG-639); this review type renders no complexity at all.
  complexity: null,
  // Coding-only too (LG-637): no artifact of this kind records executions.
  verification: null,
  postMortem: {
    difficulty: 'Again',
    answers: [
      {
        id: 'pm-went-well',
        question: 'What went well?',
        answer:
          'Back-of-envelope estimation felt smooth — got to 1B URLs and the 100:1 read ratio quickly, and the numbers actually drove the design choices.',
      },
      {
        id: 'pm-improvement',
        question: 'What could use improvement?',
        answer:
          'Completely ran out of time before partitioning and it threw me — I froze for a couple minutes near the end. How do people fit two full deep dives into 45 minutes?',
      },
    ],
    // Coding-only: nothing outside the coding editor records an approach.
    approach: null,
    lastEditedWhen: '5 days ago',
  },
  comments: [
    {
      id: 'sd-1',
      kind: 'praise',
      section: 'nfr',
      target: 'Non-functional · N2',
      source: 'ai',
      status: 'approved',
      body: 'Every NFR is quantified — p99 < 100ms, 100:1 read ratio, 1B URLs. Numbers like these are what let the design defend itself. Keep doing this.',
    },
    {
      id: 'sd-2',
      kind: 'critique',
      section: 'hld',
      target: 'High-level · Redis cache',
      source: 'ai',
      status: 'pending',
      body: 'The single Redis node is a single point of failure on your hottest path — every redirect goes through it. An interviewer will pull this thread; you need at least a replica + failover story.',
      alt: 'Strong read path overall — one gap: Redis as drawn is a SPOF on the redirect path. Add a replica/failover sentence and this section is interview-ready.',
    },
    {
      id: 'sd-3',
      kind: 'critique',
      section: 'deep',
      target: 'Deep dives · Partitioning gap',
      source: 'ai',
      status: 'pending',
      body: '1B rows with no partitioning story is the biggest gap here. Even one sentence — "partition by short-key hash, since all lookups are by key" — would have closed it. Let’s drill this pattern.',
      alt: 'You ran out of time, but partitioning here is a one-liner: all lookups are by short key, so hash-partition on it. Worth rehearsing so it’s automatic next time.',
    },
    {
      id: 'sd-4',
      kind: 'critique',
      section: 'pacing',
      target: 'Pacing · Total',
      source: 'ai',
      status: 'approved',
      // Grounded in the seed's own stored facts (LG-632): the one company row
      // it carries (Google), its topic average, and the post-mortem's account
      // of running out of time — not bands or timings the seed never held.
      body: '48:37 is past the Google band (35–45) and +7:37 over the system-design topic average. That overrun is exactly what cut the partitioning deep dive — tightening the earlier sections buys it back.',
    },
    {
      id: 'sd-5',
      kind: 'suggestion',
      section: 'postmortem',
      target: 'Post-mortem · Question',
      source: 'ai',
      status: 'pending',
      body: 'They asked how to fit two deep dives into 45 minutes — worth answering directly. Suggested framing: pick deep dives DURING requirements (flag "I’ll go deep on X and Y later"), and timebox HLD to 12 minutes.',
      alt: 'Direct answer to their question: choose your two deep dives while gathering requirements and announce them, then timebox HLD to 12 minutes so the back half is protected.',
    },
  ],
}
